import type { TrackingPath } from '@cobliteam/api-lib';
import { useMap } from '@vis.gl/react-google-maps';
import { useEffect, useMemo } from 'react';
import TrackingMapMarkers from './TrackingMapMarkers';

interface TrackingMapPathsProps {
  path: TrackingPath;
  pathWindow: {
    start: number;
    end?: number;
  };
}

const TrackingMapPaths = ({ path, pathWindow }: TrackingMapPathsProps) => {
  const map = useMap();
  const { geometry, properties } = path;

  const coordinates = geometry.coordinates;
  const timestamps = properties.timestamps;

  // Path points
  const points = useMemo(() => coordinates.map(([lng, lat]) => new google.maps.LatLng(lat, lng)), [coordinates]);

  // Highlight points
  const highlightPoints = useMemo(() => {
    const startIndex = timestamps.findIndex((ts) => ts >= pathWindow.start);
    if (pathWindow.end === undefined) {
      return [points[startIndex]];
    }

    return points.slice(
      startIndex,
      timestamps.findIndex((ts) => ts > pathWindow.end!)
    );
  }, [points, timestamps, pathWindow]);

  useEffect(() => {
    if (!(map && coordinates && timestamps)) return;

    // Create bounds that include all points
    const bounds = points.reduce((b, point) => b.extend(point), new google.maps.LatLngBounds());

    const basePolyline = new google.maps.Polyline({
      path: points,
      geodesic: true,
      strokeColor: '#000000',
      strokeWeight: 5,
      map,
    });

    const highlightPolyline = new google.maps.Polyline({
      path: highlightPoints,
      geodesic: true,
      strokeColor: '#0234E1',
      strokeOpacity: 1,
      strokeWeight: 5,
      map,
      zIndex: 2,
    });

    map.fitBounds(bounds);

    return () => {
      basePolyline.setMap(null);
      highlightPolyline.setMap(null);
    };
  }, [map, coordinates, timestamps, points, highlightPoints]);

  return <TrackingMapMarkers points={points} highlightPoints={highlightPoints} />;
};

export default TrackingMapPaths;
